import { useContext, useEffect, useMemo, useRef, ReactNode } from 'react'
import { InputAdornment } from '@mui/material'
import {
  default as MuiCoreTextField,
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField'
import { omitTypedKeys } from '@common/helpers/helpers'
import cn from 'classnames'
import { TextField as MuiRffTextField, showErrorOnBlur } from 'mui-rff'
import genericSs from '../../../assets/styles/generic.module.scss'
import { formatValue } from '../../../helpers/helpers'
import { TableCellContext } from '../TableCell/TableCell'
import styles from './TextField.module.scss'

interface TextFieldProps extends Omit<MuiTextFieldProps, 'size'> {
  size?: 'small' | 'medium'
  useFinalForm?: boolean
  inTable?: boolean
  align?: 'left' | 'center' | 'right'
  startIcon?: ReactNode
}

const TextField = ({
  inTable = false,
  useFinalForm = true,
  onChange,
  value,
  align = 'left',
  startIcon,
  ...props
}: TextFieldProps) => {
  // The field renders at a single height, so `size` selects nothing. It stays out of the
  // props handed to MUI, whose own size scale would reintroduce a second height.
  const componentProps = omitTypedKeys(props, ['size'])
  const Component = useFinalForm ? MuiRffTextField : MuiCoreTextField
  const extraProps = useFinalForm ? {} : { onChange, value }
  const context = useContext(TableCellContext)
  const isActive = useMemo(() => context?.isActive, [context])

  const inputRef = useRef<HTMLInputElement>(null)

  const evaluatedType = useMemo(() => {
    if (['percent', 'amount'].includes(props.type)) {
      return props.type
    }
    return null
  }, [props.type])

  useEffect(() => {
    if (inputRef.current && isActive && inTable) {
      inputRef.current.focus()
    }
  }, [isActive, inputRef, inTable])

  return (
    <>
      {!isActive && inTable && (
        <div
          className={cn({
            [genericSs.inactiveFormField]: !value,
            syntheticInputField: inTable,
            [styles.syntheticAlignLeft]: align === 'left',
            [styles.syntheticAlignCenter]: align === 'center',
            [styles.syntheticAlignRight]: align === 'right',
          })}
        >
          <span>
            {value
              ? formatValue({
                  value: value,
                  type: evaluatedType,
                  scaleValue: 1,
                })
              : props.placeholder}
          </span>
        </div>
      )}
      {isActive || !inTable ? (
        <Component
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mui-rff vs mui-core props conflict
          {...(componentProps as any)}
          {...extraProps}
          classes={{
            root: cn({
              tableFormField: inTable,
            }),
          }}
          inputRef={props.inputRef || inputRef}
          subscription={{
            value: true,
            focus: true,
            error: true,
            touched: true,
          }}
          slotProps={{
            ...props.slotProps,
            input: {
              // Legacy `InputProps` first: MUI v7 lets `slotProps.input` win over
              // it, so anything passed that way would otherwise be dropped
              // silently. MUI Autocomplete's renderInput supplies its popper
              // anchor `ref` and the arrow/clear `endAdornment` through
              // `InputProps` — losing them leaves an input whose dropdown can
              // never open.
              ...(props as { InputProps?: object }).InputProps,
              ...props.slotProps?.input,
              classes: {
                root: cn(styles.inputBase, {
                  [styles.multiline]: props.multiline,
                }),
                input: cn(styles.input, {
                  [styles.textRight]: align === 'right',
                }),
                error: styles.inputError,
                adornedStart: styles.adornmentStart,
                adornedEnd: styles.adornmentEnd,
              },
              disableUnderline: true,
              ...(startIcon && {
                startAdornment: <InputAdornment position="start">{startIcon}</InputAdornment>,
              }),
            },
            formHelperText: {
              ...props.slotProps?.formHelperText,
              ['data-cy' as string]: 'input-error-text',
              classes: { root: styles.formHelperTextRoot },
            },
          }}
          {...(useFinalForm && { showError: showErrorOnBlur })}
          variant="standard"
        />
      ) : null}
    </>
  )
}

export default TextField
